import type { ReactNode } from 'react';
import { useFullScreen } from '../../hooks/useFullScreen';

interface FullScreenContainerProps {
  children: ReactNode;
}

const FullScreenContainer = ({ children }: FullScreenContainerProps) => {
  const { 
    currentMode, 
    isFullScreen,
    isEditorFocused,
    isPreviewFocused, 
    isSplitMode,
    enterEditorFocus,
    enterPreviewFocus,
    setFullScreenMode,
    exitFullScreen
  } = useFullScreen();

  if (!isFullScreen) {
    return <>{children}</>;
  }

  const getModeClass = () => {
    switch (currentMode) {
      case 'editor':
        return 'full-screen-editor-only';
      case 'preview':
        return 'full-screen-preview-only';
      case 'split':
        return 'full-screen-split';
      default:
        return '';
    }
  };

  return (
    <div className={`full-screen-container ${getModeClass()}`}>
      {/* Full-screen navigation */}
      <div className="full-screen-nav">
        <button
          onClick={enterEditorFocus}
          className={isEditorFocused ? 'active' : ''}
          title="Focus Editor (Ctrl+1)"
        >
          📝 Editor
        </button>
        <button
          onClick={() => setFullScreenMode('split')}
          className={isSplitMode ? 'active' : ''}
          title="Split View (Ctrl+3)"
        >
          ⚖️ Split
        </button>
        <button
          onClick={enterPreviewFocus}
          className={isPreviewFocused ? 'active' : ''}
          title="Focus Preview (Ctrl+2)"
        >
          👁️ Preview
        </button>
        <div className="w-px h-6 bg-white/30 mx-1" />
        <button
          onClick={exitFullScreen}
          title="Exit Full-screen (Escape)"
        >
          ✕ Exit
        </button>
      </div>

      {/* Full-screen content */}
      <div className="full-screen-content">
        {children}
      </div>

      {/* Keyboard shortcuts hint */}
      <div className="full-screen-shortcuts">
        <div className="mb-1">
          <kbd>F11</kbd> Toggle • <kbd>Esc</kbd> Exit • <kbd>Ctrl+1</kbd> Editor • <kbd>Ctrl+2</kbd> Preview • <kbd>Ctrl+3</kbd> Split
        </div>
      </div>
    </div>
  );
};

export default FullScreenContainer;